import { Position, type NodeProps } from '@xyflow/react';
import EditIcon from '@mui/icons-material/Edit';
import { v4 as uuidv4 } from 'uuid';

import { type NodeItem, type ConditionalNodeData, type ParentNode } from '@models/flows';

import { useToast } from '@hooks/useToast';

import { Connector } from '@utils/flows';

import { ConnectionPoint, DeleteButton } from '@components/Flows/components/partials/index';

import s from './ConditionalNode.module.css';

type ConditionalNodeProps = NodeProps & {
  onEdit: (parentNode: ParentNode, childNode: NodeItem) => void;
  onDelete: (nodeId: string) => void;
};

function ConditionalNode({ id, type, data, onEdit, onDelete }: ConditionalNodeProps) {
  const toast = useToast();
  const conditionalData = data as unknown as ConditionalNodeData;
  const conditional = conditionalData?.items[0] || { code: '', label: '', value: [], id: uuidv4() };
  const nodeId = id;
  const values = Array.isArray(conditional.value) ? (conditional.value as string[]) : [];
  const isCatchAll = conditional.code === 'everythingElse';

  const handleNodeEdit = () => {
    if (!conditionalData) {
      toast.error('No data available for this node');
      return;
    }
    const parentNode: ParentNode = {
      id: nodeId,
      type,
      data: conditionalData,
    };

    onEdit(parentNode, conditional);
  };

  const handleDelete = () => {
    if (!nodeId) {
      toast.error('Node ID is missing');
      return;
    }

    onDelete(nodeId);
  };

  return (
    <div className={s.conditonalContainer}>
      <ConnectionPoint type={Connector.Target} position={Position.Left} id="incoming" />
      <ConnectionPoint type={Connector.Source} position={Position.Right} id="positive" />
      <ConnectionPoint type={Connector.Source} position={Position.Bottom} id="negative" />
      <h1 className={s.title}>Conditional Node</h1>
      <div className={s.conditionalBlock}>
        <div className={s.conditionalLineWrapper}>
          {conditional.label && <span className={s.typeTag}>{conditional.label}</span>}
          {!isCatchAll && (
            <p className={s.conditionalLabel}>
              {values.length > 0 ? values.join(', ') : 'No condition set'}
            </p>
          )}
        </div>
        <button type="button" className={s.editButton} onClick={handleNodeEdit}>
          <EditIcon />
        </button>
      </div>
      <DeleteButton onDelete={handleDelete} />
    </div>
  );
}

export default ConditionalNode;
